import type { Permission } from '@hostesa/auth-client';
import { useAuth } from '@hostesa/auth-client';
import type { FeatureFlag } from '@hostesa/feature-flags';
import { useFlag } from '@hostesa/feature-flags';
import type { ReactNode } from 'react';
import { NoAccessPage } from '../routes/standalone-pages';

/**
 * Per-route RBAC + feature-flag gate, applied inside the operator layout.
 *
 *   allowed = can(permission) && (flag ? flagEnabled : true)
 *
 * `can()` is bound (by OperatorGuard) to the operator's `hostesa_admin`
 * membership, so this is a pure RBAC check — every cross-tenant route passes
 * `admin:cross_tenant_read`. Provider diagnostics additionally pass the
 * `backoffice_provider_diagnostics` flag.
 *
 * A failure renders the opaque no-access state. The backend remains the
 * authoritative boundary; any 403/404 it returns is handled identically.
 */
export function OperatorRoute({
  permission,
  flag,
  children,
}: {
  permission: Permission;
  flag?: FeatureFlag;
  children: ReactNode;
}) {
  const { can } = useAuth();
  // Read a stable flag; only enforced when `flag` is provided.
  const flagEnabled = useFlag(flag ?? 'backoffice_provider_diagnostics');

  const allowed = can(permission) && (flag ? flagEnabled : true);

  if (!allowed) return <NoAccessPage />;
  return <>{children}</>;
}
